import React, { useEffect, useState } from 'react';
import { Calendar, Plus, Edit2, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { conciergeAPI, ConciergeReservation } from '../../api/concierge';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { ReservationModal } from './ReservationModal';
import { formatters } from '../../utils/formatters';

interface ReservationsListProps {
  propertyId: number;
  year: number;
  month: number;
}

export const ReservationsList: React.FC<ReservationsListProps> = ({
  propertyId,
  year,
  month,
}) => {
  const [reservations, setReservations] = useState<ConciergeReservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<ConciergeReservation | null>(null);
  const [prefilledStartDate, setPrefilledStartDate] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: number | null;
  }>({ isOpen: false, id: null });

  useEffect(() => {
    loadReservations();
  }, [propertyId, year, month]);

  const loadReservations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await conciergeAPI.getReservations(propertyId, year, month);
      setReservations(data);
    } catch (err) {
      setError('Erreur lors du chargement des réservations');
      console.error('Error loading reservations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingReservation(null);
    setPrefilledStartDate(null);
    setIsModalOpen(true);
  };

  const handleAddWithDate = (day: number) => {
    setEditingReservation(null);
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setPrefilledStartDate(dateStr);
    setIsModalOpen(true);
  };

  const handleEdit = (reservation: ConciergeReservation) => {
    setEditingReservation(reservation);
    setPrefilledStartDate(null);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;

    try {
      await conciergeAPI.deleteReservation(deleteConfirm.id);
      await loadReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      setError('Erreur lors de la suppression de la réservation');
    } finally {
      setDeleteConfirm({ isOpen: false, id: null });
    }
  };

  const handleSave = async () => {
    await loadReservations();
    setIsModalOpen(false);
    setPrefilledStartDate(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  const totalRevenue = reservations.reduce((sum, res) => sum + (Number(res.total_cost) || 0), 0);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

 const getDateReservationStatus = (day: number): 'free' | 'reserved' | 'pre-reserved' | 'past' => {
    const currentDate = new Date(year, month - 1, day);
    currentDate.setHours(0, 0, 0, 0);  // ← Ajout
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const reservation = reservations.find(reservation => {
      const startDate = new Date(reservation.start_date);
      startDate.setHours(0, 0, 0, 0);  // ← Ajout important
      
      const endDate = new Date(reservation.end_date);
      endDate.setHours(0, 0, 0, 0);    // ← Ajout important
      
      return currentDate >= startDate && currentDate < endDate;
    });

    if (!reservation) return 'free';

    const reservationEndDate = new Date(reservation.end_date);
    reservationEndDate.setHours(0, 0, 0, 0);  // ← Ajout

    if (reservationEndDate < today) {
      return 'past';
    }

    return reservation.is_pre_reservation ? 'pre-reserved' : 'reserved';
  };

  const getReservationForDay = (day: number): ConciergeReservation | undefined => {
    const currentDate = new Date(year, month - 1, day);
    currentDate.setHours(0, 0, 0, 0);  // ← Ajout
    
    return reservations.find(reservation => {
      const startDate = new Date(reservation.start_date);
      startDate.setHours(0, 0, 0, 0);  // ← Ajout important
      
      const endDate = new Date(reservation.end_date);
      endDate.setHours(0, 0, 0, 0);    // ← Ajout important
      
      return currentDate >= startDate && currentDate < endDate;
    });
  };

  const daysInMonth = getDaysInMonth(year, month);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Réservations ({reservations.length})
          </h3>
          <div className="px-3 sm:px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center space-x-2">
              <span className="text-xs sm:text-sm text-green-600 dark:text-green-400 font-medium">
                Total du mois:
              </span>
              <span className="text-base sm:text-lg font-bold text-green-700 dark:text-green-300">
                {formatters.formatPrice(totalRevenue)} DA
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="p-3 sm:p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors touch-manipulation self-end sm:self-auto"
          title="Ajouter une réservation"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Calendrier du mois</h4>
        <div className="grid grid-cols-7 gap-2">
          {daysArray.map((day) => {
            const status = getDateReservationStatus(day);
            const reservation = getReservationForDay(day);
            return (
              <div
                key={day}
                onClick={() => {
                  if (reservation) {
                    handleEdit(reservation);
                  } else {
                    handleAddWithDate(day);
                  }
                }}
                className={`p-2 text-center rounded-lg border ${
                  status === 'reserved'
                    ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 font-semibold cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/50'
                    : status === 'pre-reserved'
                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 font-semibold cursor-pointer hover:bg-orange-200 dark:hover:bg-orange-900/50'
                    : status === 'past'
                    ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 font-semibold cursor-pointer hover:bg-green-200 dark:hover:bg-green-900/50'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600'
                } transition-colors`}
              >
                <span className="text-sm">{day}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-2 mt-3 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700"></div>
            <span className="text-gray-600 dark:text-gray-400">Réservé</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700"></div>
            <span className="text-gray-600 dark:text-gray-400">Pré-réservé</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700"></div>
            <span className="text-gray-600 dark:text-gray-400">Passée</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Libre</span>
          </div>
        </div>
      </div>

      {reservations.length > 0 && (
        <>
          {/* Vue Mobile : Cards */}
          <div className="sm:hidden space-y-3">
            {reservations.map((reservation) => (
              <div
                key={reservation.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {reservation.guest_name}
                    </p>
                    {reservation.guest_phone && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                        {reservation.guest_phone}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    <button
                      onClick={() => handleEdit(reservation)}
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 touch-manipulation"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(reservation.id!)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 touch-manipulation"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {formatters.formatDate(reservation.start_date)} - {formatters.formatDate(reservation.end_date)}
                  </span>
                  <span className="font-medium">{reservation.nights_count} nuits</span>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Prix/Nuit</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatters.formatPrice(reservation.price_per_night)} DA
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Coût Total</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatters.formatPrice(reservation.total_cost || 0)} DA
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Acompte</p>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      {formatters.formatPrice(reservation.amount_paid)} DA
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Reste</p>
                    <p className={`text-sm font-medium ${
                      (reservation.balance_due || 0) > 0
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-green-600 dark:text-green-400'
                    }`}>
                      {formatters.formatPrice(reservation.balance_due || 0)} DA
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Vue Desktop : Tableau */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Locataire
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Téléphone
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prix/Nuit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Nuits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Coût Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acompte versé
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reste
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {reservations.map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatters.formatDate(reservation.start_date)} - {formatters.formatDate(reservation.end_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {reservation.guest_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {reservation.guest_phone || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatters.formatPrice(reservation.price_per_night)} DA
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {reservation.nights_count}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                      {formatters.formatPrice(reservation.total_cost || 0)} DA
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400">
                      {formatters.formatPrice(reservation.amount_paid)} DA
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span className={`font-medium ${
                        (reservation.balance_due || 0) > 0
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {formatters.formatPrice(reservation.balance_due || 0)} DA
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(reservation)}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(reservation.id!)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ReservationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPrefilledStartDate(null);
        }}
        onSave={handleSave}
        propertyId={propertyId}
        reservation={editingReservation}
        prefilledStartDate={prefilledStartDate}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Supprimer la réservation"
        message="Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
      />
    </div>
  );
};
